//settings-page.tsx

import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import React, { useState, useContext, useEffect, useRef } from "react";
import Title from "../components/title";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Typography,
} from "@mui/material";
import LoadingBackdrop from "../components/loading-backdrop";
import { ErrorContext, UserContext } from "../components/app-routes";
import { billingApi } from "../services/api/billing";
import { settingsApi } from "../services/api/settings";
import mixpanel from "mixpanel-browser";
import { FastField, FieldProps, Form, Formik } from "formik";
import * as yup from "yup";
import { AppTextField } from "../components/text-field";
import { emailChangeApi } from "../services/api/email-change";
import TimeZoneControl from "../components/time-zone-control";
import { growersApi } from "../services/api/growers";
import Switch from "@mui/material/Switch";
import { getCustomLabel } from "../utils/custom-labels";

export default function SettingsPage(props: { onGrowerUpdated: () => void }) {
  const [busy, setBusy] = useState(false);
  const [openEmailChangeDialog, setOpenEmailChangeDialog] = useState(false);
  const { handleError } = useContext(ErrorContext);
  const context = useContext(UserContext);
  const grower = context.grower;
  const user = context.user;
  const canEditFarmSettings = !user.Invited;
  const isMounted = useRef(true);
  const [openWorkflowWarning, setOpenWorkflowWarning] = useState(false);
  const [pendingWorkflowValue, setPendingWorkflowValue] = useState<
    string | null
  >(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  if (!grower) {
    throw new Error("Invalid state");
  }

  useEffect(() => {
    mixpanel.track("View Settings Page");
  }, []);

  const handleClickBilling = async () => {
    setBusy(true);
    try {
      const session = await billingApi.createPortalSession();
      window.location.assign(session.Url);
    } catch (error) {
      handleError(error);
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  // When user selects a workflow, open the warning dialog instead of applying it immediately
  const handleChangeWorkflow = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = (event.target as HTMLInputElement).value;
    setPendingWorkflowValue(next);
    setOpenWorkflowWarning(true);
  };

  const confirmWorkflowSwitch = async () => {
    if (!pendingWorkflowValue) return;

    setBusy(true);

    try {
      await settingsApi.update({ workflow: pendingWorkflowValue });

      context.state.context!.grower!.Workflow = pendingWorkflowValue as any;
      context.setState(context.state);

      mixpanel.track("Update Workflow", { value: pendingWorkflowValue });
      mixpanel.people.set({ workflow: pendingWorkflowValue });
    } catch (error) {
      handleError(error);
    } finally {
      if (isMounted.current) setBusy(false);
    }

    setOpenWorkflowWarning(false);
    setPendingWorkflowValue(null);
  };

  const cancelWorkflowSwitch = () => {
    setOpenWorkflowWarning(false);
    setPendingWorkflowValue(null);
  };

  const onSubmitSaveGrowerName = async (
    values: { name: string },
    { resetForm }: { resetForm: (o: { values: { name: string } }) => void },
  ) => {
    setBusy(true);

    try {
      await growersApi.patch({ name: values.name });

      mixpanel.track("Change Farm Name");

      props.onGrowerUpdated();
      resetForm({ values });
    } catch (error) {
      handleError(error);
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  const onSubmitSaveEmail = async (values: { email: string }) => {
    setBusy(true);

    try {
      await emailChangeApi.change(values.email);
      setOpenEmailChangeDialog(true);
      mixpanel.track("Request Email Change");
    } catch (error) {
      handleError(error);
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  const onSubmitSaveTimeZone = async (
    values: { timeZone: string },
    { resetForm }: { resetForm: (o: { values: { timeZone: string } }) => void },
  ) => {
    setBusy(true);

    try {
      await growersApi.patch({ timeZone: values.timeZone });
      props.onGrowerUpdated();
      resetForm({ values });
    } catch (error) {
      handleError(error);
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  const onSubmitSaveStartWeekOn = async (
    values: { startWeekOn: string },
    {
      resetForm,
    }: { resetForm: (o: { values: { startWeekOn: string } }) => void },
  ) => {
    setBusy(true);

    try {
      await growersApi.patch({ startWeekOn: values.startWeekOn });

      mixpanel.track("Change Start Week", {
        value: values.startWeekOn,
      });

      props.onGrowerUpdated();
      resetForm({ values });
    } catch (error) {
      handleError(error);
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  const onSubmitSaveTasksTitle = async (
    values: { tasksTitle: string },
    {
      resetForm,
    }: {
      resetForm: (o: { values: { tasksTitle: string } }) => void;
    },
  ) => {
    setBusy(true);

    try {
      const customLabels = grower.customLabels
        ? JSON.parse(grower.customLabels)
        : {};

      customLabels.tasks = values.tasksTitle;

      await settingsApi.update({
        tasksTitle: values.tasksTitle,
        customLabels: JSON.stringify(customLabels),
      });
      props.onGrowerUpdated();

      resetForm({ values });
    } catch (error) {
      handleError(error);
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  const getCustomLabels = () => {
    try {
      return grower.customLabels ? JSON.parse(grower.customLabels) : {};
    } catch {
      return {};
    }
  };

  const onSubmitSaveCustomLabels = async (
    values: {
      tasks: string;
      orders: string;
      cropTracker: string;
      elements: string;
      seeds: string;
      crops: string;
      products: string;
      customers: string;
      deliveries: string;
      diary: string;
      reports: string;
      referrals: string;
      support: string;
      team: string;
      settings: string;
    },
    {
      resetForm,
    }: {
      resetForm: (o: {
        values: {
          tasks: string;
          orders: string;
          cropTracker: string;
          elements: string;
          seeds: string;
          crops: string;
          products: string;
          customers: string;
          deliveries: string;
          diary: string;
          reports: string;
          referrals: string;
          support: string;
          team: string;
          settings: string;
        };
      }) => void;
    },
  ) => {
    setBusy(true);

    try {
      await settingsApi.update({
        customLabels: JSON.stringify(values),
        tasksTitle: values.tasks,
      });

      mixpanel.track("Update Custom Labels", {
        hasTaskLabels: true,
        hasMenuLabels: true,
      });

      props.onGrowerUpdated();

      resetForm({ values });
    } catch (error) {
      handleError(error);
    } finally {
      if (isMounted.current) setBusy(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
      <Box>
        <Paper
          sx={{
            p: 2,
            display: "flex",
            overflow: "auto",
            flexDirection: "column",
          }}
        >
          <Title>{getCustomLabel(grower ?? null, "settings")}</Title>{" "}
          <Typography variant="h6">Profile</Typography>
          <Divider />
          <Box mt={2}>
            <Typography mt={4} variant="h6">
              Grower
            </Typography>
            <Divider />
            <Box mt={4}>
              <Formik
                initialValues={{
                  name: grower.Name,
                }}
                validationSchema={yup.object({
                  name: yup.string().required("Farm name is required"),
                })}
                onSubmit={onSubmitSaveGrowerName}
              >
                {(f) => (
                  <>
                    <Form noValidate>
                      <FastField name="name">
                        {(fieldProps: FieldProps) => (
                          <AppTextField
                            {...fieldProps}
                            label="Farm name"
                            disabled={!canEditFarmSettings}
                          />
                        )}
                      </FastField>
                      <Box mt={1}>
                        <Button
                          variant="contained"
                          color="primary"
                          type="submit"
                          disabled={
                            busy ||
                            !f.dirty ||
                            !!f.errors.name ||
                            !canEditFarmSettings
                          }
                        >
                          Save
                        </Button>
                      </Box>
                    </Form>
                  </>
                )}
              </Formik>
            </Box>

            <Formik
              initialValues={{
                email: user.Email,
              }}
              validationSchema={yup.object({
                email: yup
                  .string()
                  .email("Enter a valid email")
                  .required("Email is required"),
              })}
              onSubmit={onSubmitSaveEmail}
            >
              {(f) => (
                <>
                  <Form noValidate>
                    <FastField name="email">
                      {(fieldProps: FieldProps) => (
                        <AppTextField
                          {...fieldProps}
                          label="Email"
                          type="email"
                        />
                      )}
                    </FastField>
                    <Box mt={1}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={busy || !f.dirty || !!f.errors.email}
                      >
                        Save
                      </Button>
                    </Box>
                  </Form>
                </>
              )}
            </Formik>
          </Box>
          <Typography mt={10} variant="h6">
            Grower
          </Typography>
          <Divider />
          <Box mt={4}>
            <Formik
              initialValues={{
                timeZone: grower.TimeZone,
              }}
              validationSchema={yup.object({
                timeZone: yup.string(),
              })}
              onSubmit={onSubmitSaveTimeZone}
            >
              {(f) => (
                <>
                  <Form noValidate>
                    <TimeZoneControl
                      value={f.values.timeZone}
                      onChange={f.handleChange}
                      disabled={!canEditFarmSettings}
                    />
                    <Box mt={1}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={
                          busy ||
                          !f.dirty ||
                          !!f.errors.timeZone ||
                          !canEditFarmSettings
                        }
                      >
                        Save
                      </Button>
                    </Box>
                  </Form>
                </>
              )}
            </Formik>
          </Box>
          <Box mt={4}>
            <Formik
              initialValues={{
                startWeekOn: grower.StartWeekOn,
              }}
              validationSchema={yup.object({
                startWeekOn: yup.string(),
              })}
              onSubmit={onSubmitSaveStartWeekOn}
            >
              {(f) => (
                <>
                  <Form noValidate>
                    <FormControl fullWidth>
                      <InputLabel id="start-week-on-label">
                        Start Week On
                      </InputLabel>
                      <Select
                        variant="outlined"
                        labelId="start-week-on-label"
                        id="startWeekOn"
                        name="startWeekOn"
                        label="Start Week On"
                        value={f.values.startWeekOn}
                        onChange={f.handleChange}
                        disabled={!canEditFarmSettings}
                      >
                        <MenuItem value={"Saturday"}>Saturday</MenuItem>
                        <MenuItem value={"Sunday"}>Sunday</MenuItem>
                        <MenuItem value={"Monday"}>Monday</MenuItem>
                      </Select>
                    </FormControl>

                    <Box mt={1}>
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={
                          busy ||
                          !f.dirty ||
                          !!f.errors.startWeekOn ||
                          !canEditFarmSettings
                        }
                      >
                        Save
                      </Button>
                    </Box>
                  </Form>
                </>
              )}
            </Formik>
          </Box>
          <Typography mt={10} variant="h6">
            Billing
          </Typography>
          <Divider />
          <Box>
            <Button
              sx={{ mt: 2 }}
              variant="contained"
              color="primary"
              onClick={handleClickBilling}
            >
              Go To Billing Portal
            </Button>
          </Box>
          {/* ─────────────────────────────────────────────── */}
          {/* Batch Color Toggle */}
          {/* ─────────────────────────────────────────────── */}
          <Typography mt={10} variant="h6">
            Batch Background Coloring
          </Typography>
          <Divider />
          <Box mt={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={grower.ShowBatchColors ?? true}
                  disabled={!canEditFarmSettings}
                  onChange={async (e) => {
                    const value = e.target.checked;
                    setBusy(true);

                    try {
                      // Save to backend
                      await settingsApi.update({ ShowBatchColors: value });

                      mixpanel.track("Toggle Batch Row Colors", {
                        value: value,
                      });

                      // Update local grower state so UI reacts instantly
                      context.state.context!.grower!.ShowBatchColors = value;
                      context.setState(context.state);
                    } catch (error) {
                      handleError(error);
                    } finally {
                      setBusy(false);
                    }
                  }}
                />
              }
              label="Enable batch row colors"
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Turn row background colors on or off to help differentiate crop
              batches. This does NOT affect overdue Task (red) shading.
            </Typography>
          </Box>
          <Typography mt={10} variant="h6">
            Workflow
          </Typography>
          <Divider />
          <Box mt={2}>
            <FormControl
              sx={{
                "& label.Mui-focused": { color: "black" },
                "& label": { color: "black" },
              }}
            >
              <FormLabel>
                SeedLeaf offers two workflows: Simple or Advanced.
                <br />
                <br />
                <b>Simple:</b>
                <br /> Recommended for growers who do not need to keep exact
                records and just need Seedleaf as a crop planner.
                <br />
                <br />
                <b>Advanced:</b>
                <br />
                Recommended for growers wanting to keep careful records that are
                audit-ready. The advanced workflow is a bit more work, but
                ensures accurate crop production and packaging records.
                <br />
                <br />
                PLEASE NOTE: In the Advanced Workflow, Tasks must be completed
                for their data to appear in reports.
                <br />
                <br /> Not sure what you want to do? Learn more{" "}
                <a
                  target="_blank"
                  rel="noreferrer"
                  href="https://seedleaf.freshdesk.com/a/solutions/articles/72000625772"
                >
                  here
                </a>
                . You can change your setting at any time.
                <br />
                <br />
              </FormLabel>
              <RadioGroup
                name="controlled-radio-buttons-group"
                value={grower.Workflow}
                onChange={handleChangeWorkflow}
              >
                <FormControlLabel
                  value="simple"
                  control={<Radio />}
                  label="Simple"
                  disabled={!canEditFarmSettings}
                />
                <FormControlLabel
                  value="advanced"
                  control={<Radio />}
                  label="Advanced"
                  disabled={!canEditFarmSettings}
                />
              </RadioGroup>
            </FormControl>
          </Box>
          <Typography mt={10} variant="h6">
            Custom Labels
          </Typography>
          <Divider />
          <Formik
            initialValues={{
              tasks: getCustomLabels().tasks || "Tasks",
              orders: getCustomLabels().orders || "Orders",
              cropTracker: getCustomLabels().cropTracker || "Crop Tracker",
              elements: getCustomLabels().elements || "Elements",
              seeds: getCustomLabels().seeds || "Seeds",
              crops: getCustomLabels().crops || "Crops",
              products: getCustomLabels().products || "Products",
              customers: getCustomLabels().customers || "Customers",
              deliveries: getCustomLabels().deliveries || "Deliveries",
              diary: getCustomLabels().diary || "Diary",
              reports: getCustomLabels().reports || "Reports",
              referrals: getCustomLabels().referrals || "Referrals",
              support: getCustomLabels().support || "Support",
              team: getCustomLabels().team || "Team",
              settings: getCustomLabels().settings || "Settings",
              soak: getCustomLabels().soak || "Soak",
              sow: getCustomLabels().sow || "Sow and Cover",
              blackout: getCustomLabels().blackout || "Move to Blackout",
              uncover: getCustomLabels().uncover || "Uncover to Light",
              harvest: getCustomLabels().harvest || "Harvest",
              package: getCustomLabels().package || "Package and Live Trays",
            }}
            onSubmit={onSubmitSaveCustomLabels}
          >
            {(form) => (
              <Form>
                <Box
                  mt={2}
                  sx={{
                    display: "flex",
                    gap: 4,
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ width: 400 }}>
                    <Typography variant="h6" sx={{ mb: 4 }}>
                      Menu and Header Labels
                    </Typography>
                    <AppTextField
                      field={form.getFieldProps("tasks")}
                      form={form}
                      meta={form.getFieldMeta("tasks")}
                      label="Tasks"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("orders")}
                      form={form}
                      meta={form.getFieldMeta("orders")}
                      label="Orders"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("cropTracker")}
                      form={form}
                      meta={form.getFieldMeta("cropTracker")}
                      label="Crop Tracker"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("elements")}
                      form={form}
                      meta={form.getFieldMeta("elements")}
                      label="Elements"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("seeds")}
                      form={form}
                      meta={form.getFieldMeta("seeds")}
                      label="Seeds"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("crops")}
                      form={form}
                      meta={form.getFieldMeta("crops")}
                      label="Crops"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("products")}
                      form={form}
                      meta={form.getFieldMeta("products")}
                      label="Products"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("customers")}
                      form={form}
                      meta={form.getFieldMeta("customers")}
                      label="Customers"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("deliveries")}
                      form={form}
                      meta={form.getFieldMeta("deliveries")}
                      label="Deliveries"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("diary")}
                      form={form}
                      meta={form.getFieldMeta("diary")}
                      label="Diary"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("reports")}
                      form={form}
                      meta={form.getFieldMeta("reports")}
                      label="Reports"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("referrals")}
                      form={form}
                      meta={form.getFieldMeta("referrals")}
                      label="Referrals"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("support")}
                      form={form}
                      meta={form.getFieldMeta("support")}
                      label="Support"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("team")}
                      form={form}
                      meta={form.getFieldMeta("team")}
                      label="Team"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("settings")}
                      form={form}
                      meta={form.getFieldMeta("settings")}
                      label="Settings"
                      disabled={!canEditFarmSettings}
                    />
                  </Box>
                  <Box
                    sx={{
                      width: 400,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      Task Labels
                    </Typography>{" "}
                    <AppTextField
                      field={form.getFieldProps("soak")}
                      form={form}
                      meta={form.getFieldMeta("soak")}
                      label="Soak"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("sow")}
                      form={form}
                      meta={form.getFieldMeta("sow")}
                      label="Sow"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("blackout")}
                      form={form}
                      meta={form.getFieldMeta("blackout")}
                      label="Blackout"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("uncover")}
                      form={form}
                      meta={form.getFieldMeta("uncover")}
                      label="Uncover"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("harvest")}
                      form={form}
                      meta={form.getFieldMeta("harvest")}
                      label="Harvest"
                      disabled={!canEditFarmSettings}
                    />
                    <AppTextField
                      field={form.getFieldProps("package")}
                      form={form}
                      meta={form.getFieldMeta("package")}
                      label="Package"
                      disabled={!canEditFarmSettings}
                    />
                  </Box>
                </Box>
                <Button
                  sx={{ mt: 4, mb: 4 }}
                  type="submit"
                  variant="contained"
                  disabled={busy || !canEditFarmSettings}
                >
                  Save Labels
                </Button>
              </Form>
            )}
          </Formik>
        </Paper>
      </Box>
      <LoadingBackdrop open={busy} />
      <Dialog
        open={openWorkflowWarning}
        onClose={cancelWorkflowSwitch}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, color: "warning.main" }}>
          ⚠️ Warning!
        </DialogTitle>

        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1, mb: 2 }}>
            Switching between workflows can compromise report data.
          </Typography>

          <Typography variant="body1" sx={{ mt: 1 }}>
            Tasks completed in <strong>Simple</strong> mode will
            <strong> not </strong> populate reports in <strong>Advanced</strong>{" "}
            mode.
          </Typography>

          <Typography variant="body1" sx={{ mt: 2 }}>
            If you require accurate production records, we strongly recommend
            using (and staying in) <strong>Advanced</strong> mode.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={cancelWorkflowSwitch}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={confirmWorkflowSwitch}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openEmailChangeDialog}
        onClose={() => setOpenEmailChangeDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Email Change</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            A confirmation email has been sent to the new email address. To
            complete the change, click on the link in that email.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEmailChangeDialog(false)} autoFocus>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
